import { ChartData, TooltipItem } from "chart.js";
import "chartjs-adapter-date-fns";
import { ChartProps, Line } from "react-chartjs-2";
import { deepMerge } from "../../../helpers";
import { CpuTempInfo, extLightThemeColors } from "@app/shared";
import { format } from "date-fns";
import { useMemo } from "react";

type CpuTemperatureChartProps = {
    id?: string;
    title?: string;
    data: CpuTempInfo[] | undefined;
    chartjsOverrides?: Partial<ChartProps<"line">>;
    isLoading?: boolean;
    width?: number;
    height?: number;
};

const temperatureSeries = [
    { dataKey: "Case", accessor: "ao" as keyof CpuTempInfo },
    { dataKey: "GPU", accessor: "gpu" as keyof CpuTempInfo },
    { dataKey: "Thermal", accessor: "thermal" as keyof CpuTempInfo },
    { dataKey: "CPU", accessor: "cpu" as keyof CpuTempInfo },
];

export const CpuTemperatureChart: React.FC<CpuTemperatureChartProps> = ({ id, title, data, chartjsOverrides, isLoading, width, height = 300 }) => {
    const datasets: ChartData<"line">["datasets"] = [
        // Temperature series (left Y-axis)
        ...temperatureSeries.map((s, sIdx) => ({
            label: s.dataKey,
            data: data?.map((d) => ({ x: d.statusTimestamp! as any, y: d[s.accessor] as number })) || [],
            borderColor: extLightThemeColors[sIdx % extLightThemeColors.length],
            backgroundColor: extLightThemeColors[sIdx % extLightThemeColors.length],
            pointRadius: 0,
            yAxisID: "y",
        })),
        // Humidity series (right Y-axis)
        {
            label: "Humidity",
            data: data?.map((d) => ({ x: d.statusTimestamp! as any, y: d.humidity as number })) || [],
            borderColor: "#10B981", // Green color for humidity
            backgroundColor: "#10B981",
            pointRadius: 0,
            yAxisID: "y1",
            borderWidth: 2,
        },
    ];

    const timeZoneInfo = useMemo(() => {
        if (!data?.length) {
            return { shortName: "", longName: "", offset: "" };
        }

        const rawDate = data[0].statusTimestamp;
        // to handle both Date objects and ISO string dates from SignalR
        const aDate = rawDate instanceof Date ? rawDate : typeof rawDate === "string" ? new Date(rawDate) : new Date();

        if (isNaN(aDate.getTime())) {
            return { shortName: "", longName: "", offset: "" };
        }

        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Get all timezone info at once
        const [shortFormatter, longFormatter, offsetFormatter] = ["short", "long", "longOffset"].map(
            (type) =>
                new Intl.DateTimeFormat("en-US", {
                    timeZone,
                    timeZoneName: type as any,
                })
        );

        const getTimeZonePart = (formatter: Intl.DateTimeFormat) => formatter.formatToParts(aDate).find((part: any) => part.type === "timeZoneName")?.value ?? "";

        return {
            shortName: getTimeZonePart(shortFormatter),
            longName: getTimeZonePart(longFormatter),
            offset: getTimeZonePart(offsetFormatter),
        };
    }, [data]);

    const chartjsProps = {
        data: { datasets },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                title: {
                    display: true,
                    text: title,
                    font: {
                        size: 20,
                        weight: "bold",
                    },
                },
                legend: {
                    display: false,
                },
                tooltip: {
                    callbacks: {
                        label: function (tooltipItem: TooltipItem<"line">) {
                            const unit = tooltipItem.datasetIndex < temperatureSeries.length ? "°C" : "%";
                            return `${tooltipItem.dataset.label?.padEnd(10, " ")} ${Math.round(tooltipItem.parsed.y)} ${unit}`;
                        },
                        title: function (tooltipItems: TooltipItem<"line">[]) {
                            const date = new Date(tooltipItems[0].parsed.x);
                            const tzAbbr = new Intl.DateTimeFormat("en-US", {
                                timeZoneName: "short",
                            })
                                .formatToParts(date)
                                .find((part) => part.type === "timeZoneName")?.value;

                            return format(date, "PPpp") + " " + tzAbbr;
                        },
                    },
                },
            },
            animation: {
                duration: 500,
            },
            interaction: {
                mode: "index" as const,
                intersect: false,
            },
            scales: {
                y: {
                    type: "linear" as const,
                    display: true,
                    position: "left" as const,
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: "Temperature (°C)",
                    },
                },
                y1: {
                    type: "linear" as const,
                    display: true,
                    position: "right" as const,
                    beginAtZero: true,
                    max: 100,
                    title: {
                        display: true,
                        text: "Humidity (%)",
                    },
                    grid: {
                        drawOnChartArea: false,
                    },
                },
                x: {
                    type: "time", // Enables automatic time-based scaling
                    time: {
                        tooltipFormat: "PPpp    zzzz", // Formats tooltip using date-fns style
                    },
                    ticks: {
                        source: "auto",
                        autoSkip: true, // Prevents cluttered ticks
                        maxTicksLimit: 10, // Adjust this as needed
                    },
                    title: {
                        display: true,
                        text: `${timeZoneInfo.longName} (${timeZoneInfo.offset})`,
                    },
                },
            },
        },
    } as Partial<ChartProps<"line">>;

    const mergedProps = deepMerge(chartjsProps, chartjsOverrides || {});

    return (
        <div className="w-full p-4" style={{ height: height ? height + "px" : "330px" }}>
            <Line className="m-3" {...(mergedProps as any)} width={width} height={height} />
            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80">
                    <p className="text-gray-500 text-lg">Loading...</p>
                </div>
            )}
        </div>
    );
};
